/**
 * FathomVoyage — Bunker Price Monitor
 * Data Provider — switches between mock / scrape / paid API
 *
 * Controlled by: import.meta.env.VITE_DATA_SOURCE_BUNKER
 *   'mock'       → Always use mock data (default, safe for development)
 *   'scrape'     → ScrapingBee → Ship & Bunker (requires SCRAPINGBEE_API_KEY)
 *   'bunkerex'   → BunkerEx paid API (requires BUNKEREX_API_KEY)
 *   'platts'     → S&P Global Platts (requires PLATTS_CLIENT_ID + PLATTS_CLIENT_SECRET)
 *   'sandb'      → Ship & Bunker Direct API (requires SHIP_AND_BUNKER_API_KEY)
 */

import { getMockData }                        from './mockData.js';
import { scrapeShipAndBunker, getCachedData } from './scraper.js';
import {
  fetchFromBunkerEx,
  fetchFromPlatts,
  fetchFromShipAndBunkerApi,
}                                             from './paidApi.js';

// ---------------------------------------------------------------------------
// Source resolver
// ---------------------------------------------------------------------------

const SOURCE = (import.meta.env.VITE_DATA_SOURCE_BUNKER || 'mock').toLowerCase();

// ---------------------------------------------------------------------------
// Fallback chain
// ---------------------------------------------------------------------------

/**
 * Attempt live fetch; fall back to stale cache; fall back to mock data.
 * Always returns something — never throws to the UI layer.
 *
 * @param {Function} liveFetchFn - async function that returns live data
 * @returns {Promise<Object>} normalized data object with source + disclaimer fields
 */
async function withFallback(liveFetchFn) {
  // 1. Try live fetch
  try {
    const data = await liveFetchFn();
    return data;
  } catch (liveErr) {
    console.error('[BunkerData] Live fetch failed:', liveErr.message);
  }

  // 2. Try stale scraper cache
  const staleCache = getCachedData();
  if (staleCache) {
    console.warn(`[BunkerData] Falling back to stale cache (${staleCache.cacheAgeHours}h old)`);
    return {
      ...staleCache,
      disclaimer: `Data from ${staleCache.cacheAgeHours} hour${staleCache.cacheAgeHours !== 1 ? 's' : ''} ago — live feed temporarily unavailable`,
    };
  }

  // 3. Final fallback: mock data with warning
  console.warn('[BunkerData] No cache available — serving mock data');
  const mock = getMockData();
  return {
    ...mock,
    disclaimer: 'Demo data — live feed unavailable. Check Settings to configure your data source.',
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Main data fetcher — call this from your React components.
 *
 * @returns {Promise<{
 *   source: string,
 *   lastUpdated: string,
 *   dataDate: string,
 *   ports: Object,
 *   metrics: Object,
 *   disclaimer: string|null,
 *   cacheAgeHours: number|null
 * }>}
 */
export async function getBunkerData() {
  switch (SOURCE) {
    case 'mock':
      return getMockData();

    case 'scrape':
      return withFallback(() => scrapeShipAndBunker());

    case 'bunkerex':
      return withFallback(() => fetchFromBunkerEx());

    case 'platts':
      return withFallback(() => fetchFromPlatts());

    case 'sandb':
      return withFallback(() => fetchFromShipAndBunkerApi());

    default:
      console.error(`[BunkerData] Unknown DATA_SOURCE_BUNKER: "${SOURCE}" — defaulting to mock`);
      return getMockData();
  }
}

/**
 * Check what data source is active. Use in Settings UI.
 */
export function getActiveSource() {
  return SOURCE;
}

/**
 * Source labels for display in Settings / Data Banner
 */
export const SOURCE_LABELS = {
  mock:     { label: 'Demo Data',              color: 'amber',  live: false },
  scrape:   { label: 'Ship & Bunker (Scrape)', color: 'teal',   live: true  },
  bunkerex: { label: 'BunkerEx API',           color: 'green',  live: true  },
  platts:   { label: 'S&P Global Platts',      color: 'green',  live: true  },
  sandb:    { label: 'Ship & Bunker API',       color: 'green',  live: true  },
};
